/**
 * @fileoverview Component for the Listings Setup summary table, with rows for the Select Entities
 * task and the Specify Listings Configuartion task.
 *
 * @author Wyatt Lindquist (wlindquist@yext.com)
 */

import {EntitySelection} from '/src/com/yext/listingslaunchstorm/js/enterprise/selectentities/data/entityselection';
import {ApplePublisherId} from '/src/com/yext/listingslaunchstorm/js/enterprise/setuppublisher/data/appleconfiguration';
import {FacebookPublisherId} from '/src/com/yext/listingslaunchstorm/js/enterprise/setuppublisher/data/facebookconfiguration';
import {GooglePublisherId} from '/src/com/yext/listingslaunchstorm/js/enterprise/setuppublisher/data/gbpconfiguration';
import {YelpEnterprisePublisherId, YelpKnowledgePublisherId} from '/src/com/yext/listingslaunchstorm/js/enterprise/setuppublisher/data/yelpconfiguration';
import {DeliveryConfiguration} from '/src/com/yext/listingslaunchstorm/js/enterprise/specifylistingconfiguration/data/deliveryconfiguration';
import {ViewStepButton} from '/src/com/yext/listingslaunchstorm/js/enterprise/summary/viewstepbutton';
import {BingPublisherId, EnterpriseConfiguration, ExtendedNetworkPublisherId, LlmPublisherId} from
  '/src/com/yext/listingslaunchstorm/js/enterprise/taskflow/data/enterpriseconfiguration';
import {TaskIds} from '/src/com/yext/listingslaunchstorm/js/enterprise/taskflow/data/taskids';
import {Table} from '/ui/components/table/table';

import * as styles from '/src/com/yext/listingslaunchstorm/js/enterprise/summary/summary.module.scss';

const COLUMN_IDS = {
  STEP: 'step',
  COUNT: 'count',
  SELECTION: 'selection',
  ACTION: 'action',
};

/**
 * @param {{
 *   enterpriseConfiguration: EnterpriseConfiguration,
 *   onViewStep: function(string): void,
 *   publisherIds: Array<number>,
 * }} props
 * @returns {React.ReactNode}
 */
export function ListingsSetupTable({
  enterpriseConfiguration,
  onViewStep,
  publisherIds,
}) {
  return (
    <Table
      label={yext.msg('Summary of completed Listings Setup steps')}
      columns={getColumns()}
      dataRows={getRows(enterpriseConfiguration, onViewStep, publisherIds)}
    />
  );
}

/** @returns {!Array<?>} !Array<Table.Types.Column> */
function getColumns() {
  return [
    {
      id: COLUMN_IDS.STEP,
      label: yext.msg('Step'),
    },
    {
      id: COLUMN_IDS.COUNT,
      label: yext.msg('Count'),
    },
    {
      id: COLUMN_IDS.SELECTION,
      label: yext.msg('Selection'),
    },
    {
      id: COLUMN_IDS.ACTION,
      label: undefined,
    },
  ];
}

/**
 * @param {EnterpriseConfiguration} enterpriseConfiguration
 * @param {function(string): void} onViewStep
 * @param {Array<number>} publisherIds
 * @returns {!Array<?>} !Array<Table.Types.Row>
 */
function getRows(enterpriseConfiguration, onViewStep, publisherIds) {
  return [
    {
      id: 'selectentities',
      [COLUMN_IDS.STEP]: yext.msg('Select Entities'),
      [COLUMN_IDS.COUNT]: enterpriseConfiguration.entitySelection?.entityCount,
      [COLUMN_IDS.SELECTION]: getSelectEntitiesContent(enterpriseConfiguration.entitySelection),
      [COLUMN_IDS.ACTION]: (<ViewStepButton onClick={() => onViewStep(TaskIds.SELECT_ENTITIES)} />),
    },
    {
      id: 'specifylistingsconfiguration',
      [COLUMN_IDS.STEP]: yext.msg('Specify Listings Configuration'),
      [COLUMN_IDS.COUNT]: '--',
      [COLUMN_IDS.SELECTION]: getSpecifyListingsConfigurationContent(
        enterpriseConfiguration, publisherIds),
      [COLUMN_IDS.ACTION]: (<ViewStepButton onClick={() => onViewStep(TaskIds.SPECIFY_LISTING_CONFIGURATION)} />),
    },
  ];
}

/**
 * @param {EntitySelection | undefined} entitySelection
 * @returns {React.ReactNode}
 */
function getSelectEntitiesContent(entitySelection) {
  return (
    <>
      {yext.msg('Saved Filter: ')}
      {entitySelection?.displayName}
    </>
  );
}

/**
 * @param {EnterpriseConfiguration} enterpriseConfiguration
 * @param {Array<number>} publisherIds
 * @returns {React.ReactNode}
 */
function getSpecifyListingsConfigurationContent(enterpriseConfiguration, publisherIds) {
  const listingsConfig = enterpriseConfiguration.listingConfiguration;
  const publisherIdsWithExtendedNetwork = processPublisherIds(publisherIds);
  if (!listingsConfig) {
    return undefined;
  } else {
    const altBrandId = listingsConfig.profileField?.id;
    const altBrandName = listingsConfig.profileField?.name;
    const listingsUpdateSelectedPubs = getListingsUpdateEnabledPubs(
      listingsConfig.deliveryConfigurationMap,
      publisherIdsWithExtendedNetwork).join(', ');
    const listingsCreationSelectedPubs = getListingsCreationEnabledPubs(
      listingsConfig.deliveryConfigurationMap,
      publisherIdsWithExtendedNetwork).join(', ');
    const automaticallyOptInEnabledPubs = getAutomaticallyOptInEnabledPubs(
      enterpriseConfiguration,
      listingsConfig.deliveryConfigurationMap,
      publisherIdsWithExtendedNetwork).join(', ');
    return (
      <>
        <div>
          {altBrandId
            && <SelectedValue
              label={yext.msg('Alternate Brand Name Fields: ')}
              value={altBrandName}
            />}
          {listingsUpdateSelectedPubs.length > 0
            && <SelectedValue
              label={yext.msg('Listing Updates: ')}
              value={listingsUpdateSelectedPubs}
            />}
          {listingsCreationSelectedPubs.length > 0
            && <SelectedValue
              label={yext.msg('Listing Creation: ')}
              value={listingsCreationSelectedPubs}
            />}
          {automaticallyOptInEnabledPubs.length > 0
            && <SelectedValue
              label={yext.msg('Automatically Opt in: ')}
              value={automaticallyOptInEnabledPubs}
            />}
        </div>
      </>
    );
  }
}

/**
 * @param {Map<number, DeliveryConfiguration>} configs
 * @param {Array<number>} publisherIds
 */
function getListingsUpdateEnabledPubs(configs, publisherIds) {
  const enabledPubs = [];
  publisherIds.forEach(pubId => {
    if (!configs.get(pubId)?.dontSyncListings && getDisplayName(pubId)) {
      enabledPubs.push(getDisplayName(pubId));
    }
  });
  return enabledPubs;
}

/**
 * @param {Map<number, DeliveryConfiguration>} configs
 * @param {Array<number>} publisherIds
 */
function getListingsCreationEnabledPubs(configs, publisherIds) {
  const enabledPubs = [];
  publisherIds.forEach(pubId => {
    if (!configs.get(pubId)?.dontCreateNewListings && getDisplayName(pubId)) {
      enabledPubs.push(getDisplayName(pubId));
    }
  });
  return enabledPubs;
}

/**
 * @param {EnterpriseConfiguration} enterpriseConfiguration
 * @param {Map<number, DeliveryConfiguration>} configs
 * @param {Array<number>} publisherIds
 */
function getAutomaticallyOptInEnabledPubs(enterpriseConfiguration, configs, publisherIds) {
  const enabledPubs = [];
  publisherIds.forEach(pubId => {
    const publisherEnterpriseConfiguration = enterpriseConfiguration.getEnterpriseConfigurationForPublisherId(pubId);
    if (configs.get(pubId)?.automaticallyOptIn && !publisherEnterpriseConfiguration?.skipSetup && getDisplayName(pubId)) {
      enabledPubs.push(getDisplayName(pubId));
    }
  });
  return enabledPubs;
}

function SelectedValue({label, value}) {
  return (
    <div className={styles.specifyListingsConfigurationContent}>
      <b>{label}</b>
      {value}
    </div>
  );
}

function getDisplayName(publisherId) {
  switch (publisherId) {
    case ExtendedNetworkPublisherId:
      return yext.msg('Extended Network');
    case LlmPublisherId:
      return yext.msg('Generative AI Publishers');
    case FacebookPublisherId:
      return 'Facebook';
    case GooglePublisherId:
      return 'Google Business Profile';
    case BingPublisherId:
      return 'Bing';
    case ApplePublisherId:
      return 'Apple';
    case YelpEnterprisePublisherId:
      return 'Yelp';
    default:
      return null;
  }
}

/** @returns {!Array<number>} */
function processPublisherIds(publisherIds) {
  // As long as the account has either Yelp Enterprise or Yelp Knowledge, we will show one row
  // for Yelp that applies for both. Here we manipulate the array to make sure it only
  // contains the Yelp Enterprise publisher ID if it has either.
  const YELP_PUB_IDS = [YelpEnterprisePublisherId, YelpKnowledgePublisherId];
  const includeYelp = publisherIds.some(pubId => YELP_PUB_IDS.includes(pubId));
  const publisherIdsWithoutYelp = publisherIds.filter(pubId => !YELP_PUB_IDS.includes(pubId));
  const corePublishers = (includeYelp)? publisherIdsWithoutYelp.concat(YelpEnterprisePublisherId) : publisherIds;

  return corePublishers.concat(ExtendedNetworkPublisherId, LlmPublisherId);
}
